/**
 * ReportToolbar - 报告操作工具栏组件
 * 包含重置、复制、导出、保存、生成报告等操作
 * 支持通过 customActions/customExportConfig 自定义操作列表和导出配置
 */
import React from 'react';
import { List, Dropdown } from 'tea-component';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { IReportToolbarProps } from '../types';
import {
  getReportToolbarActions,
  EReportActions,
  EXPORT_FILE_TYPE_CONFIG,
} from '../constants';
// 使用原有图标资源
import defaultCopyIcon from '@/pages/aigc/assets/svg/agent-ds-copy.svg';
import defaultExportIcon from '@/pages/aigc/assets/svg/agent-ds-export.svg';
import defaultSaveIcon from '@/pages/aigc/assets/svg/agent-ds-save.svg';
import defaultRefreshIcon from '@/pages/aigc/assets/svg/agent-ds-refresh.svg';
import './ReportToolbar.scss';

export const ReportToolbar: React.FC<IReportToolbarProps> = ({
  activeStep,
  streamingState,
  showCopyToast,
  onActionClick,
  onExport,
  customActions,
  customExportConfig,
  exportActionKey,
  exportDisabled,
  disabledOnStreamingKey,
  disabledOnStreamingKeys,
  className,
}) => {
  const { locale } = useIntl();
  const isEn = locale === 'en-US';

  // 导出操作的 key
  const exportKey = exportActionKey || EReportActions.ReportExport;
  // 流式状态下禁用的操作 key 集合（优先使用数组，兼容单个 key）
  const disabledKeys = disabledOnStreamingKeys
    || (disabledOnStreamingKey ? [disabledOnStreamingKey] : [EReportActions.ImplementGenerate]);
  // 导出文件类型配置
  const exportConfig = customExportConfig || EXPORT_FILE_TYPE_CONFIG;
  // 获取当前步骤对应的操作列表
  const actionsList = customActions
    ? customActions.filter((item) => item.value === activeStep)
    : getReportToolbarActions(isEn, streamingState, showCopyToast, {
      refreshIcon: defaultRefreshIcon,
      copyIcon: defaultCopyIcon,
      exportIcon: defaultExportIcon,
      saveIcon: defaultSaveIcon,
    }).filter((item) => item.value === activeStep);

  const handleActionClick = (action: { key: string; }) => {
    // 指定操作在流式状态下禁用
    if (
      streamingState &&
      streamingState !== 'complete' &&
      disabledKeys.includes(action.key)
    ) {
      return;
    }
    onActionClick(action);
  };

  return (
    <div className={classNames('dba-report-toolbar', className)}>
      {actionsList.map((item, index) => (
        <div
          className="dba-report-toolbar__item"
          key={index}
          // 阻止拖拽冒泡
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => {
            if (item.key !== exportKey) {
              handleActionClick(item);
            }
          }}
        >
          {item.key !== exportKey && item.label}
          {item.key === exportKey && (
            <Dropdown
              appearance="pure"
              trigger="hover"
              clickClose={false}
              disabled={exportDisabled}
              overlayClassName="dba-report-toolbar-export-overlay"
              button={<>{item.label}</>}
            >
              {() => (
                <List type="option" style={{ maxWidth: '300px' }}>
                  {exportConfig.map((fileType) => (
                    <List.Item
                      key={fileType.value}
                      onClick={() => onExport(fileType.value)}
                    >
                      <div className="dba-report-toolbar-export-item">
                        {fileType.icon && (
                          <img
                            src={fileType.icon}
                            alt=""
                            className="dba-report-toolbar-export-item__icon"
                          />
                        )}
                        <span>{isEn ? fileType.labelEn : fileType.label}</span>
                      </div>
                    </List.Item>
                  ))}
                </List>
              )}
            </Dropdown>
          )}
        </div>
      ))}
    </div>
  );
};

export default ReportToolbar;
